"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
// Tree-shaken icon imports for optimal bundle size
import ChefHat from "lucide-react/dist/esm/icons/chef-hat";
import User from "lucide-react/dist/esm/icons/user";
import BookOpen from "lucide-react/dist/esm/icons/book-open";
import LogOut from "lucide-react/dist/esm/icons/log-out";
import Menu from "lucide-react/dist/esm/icons/menu";
import Sparkles from "lucide-react/dist/esm/icons/sparkles";
import FileText from "lucide-react/dist/esm/icons/file-text";
import PlusCircle from "lucide-react/dist/esm/icons/plus-circle";
import FileEdit from "lucide-react/dist/esm/icons/file-edit";
import { useAuth } from "@/contexts/AuthContext";
import { useRecipe } from "@/contexts/RecipeContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Navigation = () => {
  const pathname = usePathname();
  const { user, signOut } = useAuth();
  const { newlyCompletedRecipes } = useRecipe();
  const { plan } = useSubscription();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const isTester = plan?.code === 'TESTER';

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      toast({
        title: "שגיאה ביציאה",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "יציאה הושלמה",
        description: "להתראות!",
      });
    }
  };

  return (
    <nav className="w-full bg-card/80 backdrop-blur-sm border-b border-border sticky top-0 z-50">
      <div className="w-full px-4 py-3">
        <div className="flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2 text-xl md:text-2xl font-bold text-primary" aria-label="דף הבית - פידלי">
            <ChefHat className="h-6 w-6 md:h-8 md:w-8" />
            <span>פידלי</span>
          </Link>

          {isMobile && user ? (
            <DropdownMenu dir="rtl">
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-11 h-11 min-h-[44px] min-w-[44px] bg-orange-50 hover:bg-orange-100 text-orange-600 hover:text-orange-700 rounded-lg transition-all duration-200 focus:outline-none"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-64 bg-white border border-orange-200 shadow-xl rounded-2xl p-3 mt-3"
                sideOffset={12}
              >
                <DropdownMenuItem asChild>
                  <Link href="/" className="flex items-center gap-4 cursor-pointer px-5 py-4 text-lg font-medium text-gray-700 hover:bg-orange-50 hover:text-orange-700 rounded-xl transition-colors" aria-label="עבור לדף יצירת מתכון">
                    <Sparkles className="h-6 w-6 text-orange-600" />
                    <span>צור מתכון</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/profile" className="flex items-center gap-4 cursor-pointer px-5 py-4 text-lg font-medium text-gray-700 hover:bg-orange-50 hover:text-orange-700 rounded-xl transition-colors" aria-label="עבור לדף המטבח שלי - ניהול מצרכים והגדרות">
                    <User className="h-6 w-6 text-orange-600" />
                    <span>המטבח שלי</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/cookbook" className="flex items-center justify-between w-full cursor-pointer px-5 py-4 text-lg font-medium text-gray-700 hover:bg-orange-50 hover:text-orange-700 rounded-xl transition-colors" aria-label="עבור לספר המתכונים השמורים שלי">
                    <div className="flex items-center gap-4">
                      <BookOpen className="h-6 w-6 text-orange-600" />
                      <span>ספר המתכונים</span>
                    </div>
                    {newlyCompletedRecipes.size > 0 && (
                      <span className="w-4 h-4 rounded-full bg-orange-500 border-2 border-white shadow-sm" />
                    )}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/articles" className="flex items-center gap-4 cursor-pointer px-5 py-4 text-lg font-medium text-gray-700 hover:bg-orange-50 hover:text-orange-700 rounded-xl transition-colors" aria-label="עבור לדף המאמרים - טיפים ומידע על בישול">
                    <FileText className="h-6 w-6 text-orange-600" />
                    <span>מאמרים</span>
                  </Link>
                </DropdownMenuItem>
                {isTester && (
                  <>
                    <DropdownMenuItem asChild>
                      <Link href="/articles/create" className="flex items-center gap-4 cursor-pointer px-5 py-4 text-lg font-medium text-gray-700 hover:bg-orange-50 hover:text-orange-700 rounded-xl transition-colors" aria-label="עבור לדף יצירת מאמר חדש">
                        <PlusCircle className="h-6 w-6 text-orange-600" />
                        <span>צור מאמר</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/articles/my-drafts" className="flex items-center gap-4 cursor-pointer px-5 py-4 text-lg font-medium text-gray-700 hover:bg-orange-50 hover:text-orange-700 rounded-xl transition-colors" aria-label="עבור לדף הטיוטות שלי - מאמרים שמורים">
                        <FileEdit className="h-6 w-6 text-orange-600" />
                        <span>הטיוטות שלי</span>
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem
                  onClick={handleSignOut}
                  className="cursor-pointer px-5 py-4 text-lg font-medium text-gray-700 hover:bg-red-50 hover:text-red-700 rounded-xl transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <LogOut className="h-6 w-6 text-red-600" />
                    <span>יציאה</span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-2 md:gap-4">
              {user ? (
                <>
                  <Button
                    variant={pathname === "/profile" ? "default" : "ghost"}
                    asChild
                    className="text-base px-4 py-2 min-h-[44px]"
                  >
                    <Link href="/profile" className="flex items-center gap-2" aria-label="המטבח שלי - ניהול מצרכים והגדרות">
                      <User className="h-5 w-5" />
                      <span>המטבח שלי</span>
                    </Link>
                  </Button>

                  <Button
                    variant={pathname === "/cookbook" ? "default" : "ghost"}
                    asChild
                    className="text-base px-4 py-2 min-h-[44px] relative"
                  >
                    <Link href="/cookbook" className="flex items-center gap-2" aria-label="ספר המתכונים - המתכונים השמורים שלי">
                      <BookOpen className="h-5 w-5" />
                      <span>ספר המתכונים</span>
                      {newlyCompletedRecipes.size > 0 && (
                        <span className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-orange-500 border-2 border-background" />
                      )}
                    </Link>
                  </Button>

                  <Button
                    variant={pathname === "/articles" || pathname?.startsWith("/articles/") ? "default" : "ghost"}
                    asChild
                    className="text-base px-4 py-2 min-h-[44px]"
                  >
                    <Link href="/articles" className="flex items-center gap-2" aria-label="מאמרים - טיפים ומידע על בישול">
                      <FileText className="h-5 w-5" />
                      <span>מאמרים</span>
                    </Link>
                  </Button>

                  <Button
                    variant="ghost"
                    onClick={handleSignOut}
                    className="text-base px-4 py-2 min-h-[44px]"
                  >
                    <LogOut className="h-5 w-5" />
                    <span>יציאה</span>
                  </Button>
                </>
              ) : (
                <Button variant="default" className="text-base px-6 py-2 min-h-[44px]" asChild>
                  <Link href="/auth" className="flex items-center gap-2" aria-label="כניסה לחשבון או רישום משתמש חדש">
                    <User className="h-5 w-5" />
                    <span>כניסה / רישום</span>
                  </Link>
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};